import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {CallLog, CallLogResponse, CallLogUtils} from './call-log.service';
import {Observable} from 'rxjs/Observable';
import {PhoneNumber} from './phone-number.service';
import {RequestOptions, ResponseContentType} from '@angular/http';

@Injectable()
export class ReportService {

  readonly apiPath = '/api/call_event/v1/report';

  constructor(private httpClient: HttpClient) {
  }

  getDetailedCalls(customerId: string, filter: CallLogFilter, limit: number): Observable<Array<CallLog>> {
    return this.httpClient
      .post<Array<CallLogResponse>>(`${this.apiPath}/detailed/customer/${customerId}/limit/${limit}`, filter)
      .map((res: Array<CallLogResponse>) => res.map((callLog: CallLogResponse) => CallLog.fromJson(callLog)))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }

  getSummaryCalls(customerId: string, filter: CallLogFilter): Observable<Array<CallLogSummary>> {
    return this.httpClient
      .post<Array<CallLogSummaryResponse>>(`${this.apiPath}/summary/customer/${customerId}`, filter)
      .map((res: Array<CallLogSummaryResponse>) => res.map((summary: CallLogSummaryResponse) => CallLogSummary.fromJson(summary)))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }

  getSummaryCallsByDate(customerId: string, filter: CallLogFilter): Observable<Array<CallLogSummaryByDate>> {
    return this.httpClient
      .post<Array<CallLogSummaryByDateResponse>>(`${this.apiPath}/summary/customer/${customerId}/by_date`, filter)
      .map((res: Array<CallLogSummaryByDateResponse>) =>
        res.map((summary: CallLogSummaryByDateResponse) => CallLogSummaryByDate.fromJson(summary)))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }

  getDetailedXlsCalls(customerId: string, filter: CallLogFilter): Observable<any> {
    return this.httpClient
      .post(`${this.apiPath}/detailed/xls/customer/${customerId}`, filter, {responseType: 'blob'})
      .map((res) => res)
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }

  getSummaryXlsCalls(customerId: string, filter: CallLogFilter): Observable<any> {
    return this.httpClient
      .post(`${this.apiPath}/summary/xls/customer/${customerId}`, filter, {responseType: 'blob'})
      .map((res) => res)
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }

  getDetailedPdfCalls(customerId: string, filter: CallLogFilter): Observable<any> {
    return this.httpClient
      .post(`${this.apiPath}/detailed/pdf/customer/${customerId}`, filter, {responseType: 'blob'})
      .map((res) => res)
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }

  getSummaryPdfCalls(customerId: string, filter: CallLogFilter): Observable<any> {
    return this.httpClient
      .post(`${this.apiPath}/summary/pdf/customer/${customerId}`, filter, {responseType: 'blob'})
      .map((res) => res)
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }
}

export class CallLogFilter {
  constructor(public rules: Array<Array<string>>) {
  }
}

export class CallLogSummary implements CallLogSummaryResponse {

  static fromJson(json: any): CallLogSummary {
    const object: CallLogSummary = Object.create(CallLogSummary.prototype);
    const phoneNumber: PhoneNumber = Object.create(PhoneNumber.prototype);
    Object.assign(object, json);
    Object.assign(phoneNumber, json['phoneNumber']);
    object.phoneNumber = phoneNumber;
    object.callsCountByType = CallLogUtils.extractToMapOfCallTariffTypes(object.callsCountByType);
    object.callsCostSumByType = CallLogUtils.extractToMapOfCallTariffTypes(object.callsCostSumByType);
    object.callsConversationSumByType = CallLogUtils.extractToMapOfCallTariffTypes(object.callsConversationSumByType);
    return object
  }

  constructor(public phoneNumber: PhoneNumber, public costCenterId: string, public costCenterName: string,
              public departmentId: string, public departmentName: string,
              public callsCountByType: Map<string, Map<string, number>>,
              public callsCostSumByType: Map<string, Map<string, number>>,
              public callsConversationSumByType: Map<string, Map<string, number>>) {

  }

  getTotalCount(type: string): number {
    return this.sumMapValues(type, this.callsCountByType);
  }

  getTotalCost(type: string): number {
    return this.sumMapValues(type, this.callsCostSumByType);
  }

  getTotalConversation(type: string): number {
    return this.sumMapValues(type, this.callsConversationSumByType);
  }

  sumMapValues(type: string, map: Map<string, Map<string, number>>): number {
    let result = 0;
    const values = map.get(type);
    if (values) {
      values.forEach((value) => {
        result += value;
      });
    }
    return result;
  }

}

export class CallLogSummaryByDate implements CallLogSummaryByDateResponse {

  static fromJson(json: any): CallLogSummaryByDate {
    const object: CallLogSummaryByDate = Object.create(CallLogSummaryByDate.prototype);
    Object.assign(object, json);
    object.date = CallLogUtils.extractBackEndDate(json['date']);
    object.callsCountByType = CallLogUtils.extractToMapOfCallTariffTypes(object.callsCountByType);
    object.callsCostSumByType = CallLogUtils.extractToMapOfCallTariffTypes(object.callsCostSumByType);
    object.callsConversationSumByType = CallLogUtils.extractToMapOfCallTariffTypes(object.callsConversationSumByType);
    return object
  }

  constructor(public date: number,
              public callsCountByType: Map<string, Map<string, number>>,
              public callsCostSumByType: Map<string, Map<string, number>>,
              public callsConversationSumByType: Map<string, Map<string, number>>) {
  }
}


export interface CallLogSummaryResponse {
  phoneNumber: PhoneNumber,
  costCenterId: string,
  costCenterName: string,
  departmentId: string,
  departmentName: string,
  callsCountByType: any,
  callsCostSumByType: any,
  callsConversationSumByType: any,
}

export interface CallLogSummaryByDateResponse {
  date: any,
  callsCountByType: any,
  callsCostSumByType: any,
  callsConversationSumByType: any,
}





// WEBPACK FOOTER //
// /Users/tabelini/development/jvm/tenacity/pysix/PysixCloud/src/app/report.service.ts